import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { Theme, alpha, useTheme } from "@mui/material/styles";
import React, { useCallback, useEffect, useMemo } from "react";

import LogoLoader from "../../common/LogoLoader";
import { useMatrix } from "../../../hooks/queries/useMatrix";
import { monospaceStyle } from "../../../theme";
import { Matrix } from "../../../types/analytics";
import CompareTrayBar from "../../Compare/CompareTrayBar";
import { useCapturing } from "../../../utils/export/ExportStage";
import { AnalyticsPaneProps } from "../analyticsPanes";
import {
  ANALYTICS_VIEW_LABELS,
  MatrixSettings,
  changeMatrixSettings,
  selectionOf,
  toCapabilityMapParams,
  toMatrixParams,
} from "../analyticsState";
import { captionOf, pagerText } from "./matrixModel";
import MatrixCapabilities from "./MatrixCapabilities";
import MatrixControls from "./MatrixControls";
import { SHADE_ALPHA, StanceMark, thinHatchOf } from "./MatrixRows";
import MatrixTable from "./MatrixTable";

// Text with its figures in the mono face; the characters are the text's own
const WithFigures: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split(/(\d+)/).map((part, position) =>
      /^\d+$/.test(part) ? (
        // eslint-disable-next-line react/no-array-index-key
        <Box component="span" key={position} sx={monospaceStyle}>
          {part}
        </Box>
      ) : (
        part
      ),
    )}
  </>
);

const Swatch: React.FC<{ fill?: string; image?: string }> = ({ fill, image }) => (
  <Box
    component="span"
    aria-hidden
    style={{ backgroundColor: fill }}
    sx={{
      backgroundImage: image ?? "none",
      display: "inline-block",
      width: 18,
      height: 12,
      mx: "2px",
      borderRadius: "3px",
      verticalAlign: "middle",
      boxSizing: "border-box",
      border: "1px solid",
      borderColor: "divider",
    }}
  />
);

// What the marks and the shades of the table say
const MatrixLegend: React.FC<{ matrix: Matrix }> = ({ matrix }) => {
  const theme = useTheme();
  const item = { display: "inline" } as const;
  return (
    <Box
      role="list"
      aria-label="Legend"
      sx={{
        display: "flex",
        columnGap: 2,
        rowGap: 0.5,
        flexWrap: "wrap",
        alignItems: "center",
        fontSize: 12,
        color: "text.secondary",
      }}
    >
      {matrix.rows === "products" ? (
        <>
          <Box role="listitem" sx={item}>
            <StanceMark stance="has" decorative /> claimed, the conditions and modes behind it
            on hover
          </Box>
          <Box role="listitem" sx={item}>
            <StanceMark stance="requires" decorative /> stated as required, a host&rsquo;s need
          </Box>
          <Box role="listitem" sx={item}>
            <StanceMark stance="lacks" decorative /> stated as lacking
          </Box>
          <Box role="listitem" sx={item}>
            a folded group shows one mark for any of its columns
          </Box>
        </>
      ) : (
        <>
          <Box role="listitem" sx={item}>
            shade = the cell&rsquo;s share of its column: fewer{" "}
            {SHADE_ALPHA.map((opacity) => (
              <Swatch key={opacity} fill={alpha(theme.palette.accent.main, opacity)} />
            ))}{" "}
            more
          </Box>
          <Box role="listitem" sx={item}>
            <Swatch image={thinHatchOf(theme)} /> under the floor of {matrix.floors.min_support}{" "}
            products
          </Box>
          <Box role="listitem" sx={item}>
            <Box component="span" sx={{ color: "chart.need", fontSize: 10 }}>
              n
            </Box>{" "}
            stated as required or lacking
          </Box>
          <Box role="listitem" sx={item}>
            a folded group shows a mark where any of its columns counts
          </Box>
        </>
      )}
    </Box>
  );
};

// The panel the design sets the table in
const panelSx = (theme: Theme) => ({
  flex: "1 1 auto",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  boxSizing: "border-box",
  p: "16px 20px 18px",
  border: 1,
  borderColor: "divider",
  borderRadius: `${theme.radius.surface}px`,
  backgroundColor: "background.paper",
});

// The matrix: one table of rows against columns over the scope, the products
// and what they claim, or the classes, makers or capabilities and how many
// products they count. The rows are narrowed and paged here; the columns are
// counted over the whole scope whatever the rows.
const MatrixView: React.FC<AnalyticsPaneProps> = ({ state, onChange, onOpenEntity }) => {
  const settings = state.settings.matrix;
  const selection = useMemo(() => selectionOf(state), [state]);
  const params = useMemo(() => toMatrixParams(state), [state]);
  // The selection as the capability map reads it: the picker's options
  const scope = useMemo(() => toCapabilityMapParams(state), [state]);
  const read = useMatrix(params);
  const data: Matrix | null = read.data ?? null;

  const setSettings = useCallback(
    (change: Partial<MatrixSettings>) => {
      onChange({
        settings: { ...state.settings, matrix: changeMatrixSettings(settings, change) },
      });
    },
    [onChange, settings, state.settings],
  );

  // A claiming or a pin the read echoes as none names nothing shown: the
  // address lets go of it
  useEffect(() => {
    if (!data || read.isPlaceholderData) {
      return;
    }
    const stale: Partial<MatrixSettings> = {};
    if (settings.claiming && data.claiming === null) {
      stale.claiming = null;
    }
    if (settings.pin && data.pin === null) {
      stale.pin = null;
    }
    if (Object.keys(stale).length > 0) {
      setSettings(stale);
    }
  }, [data, read.isPlaceholderData, settings.claiming, settings.pin, setSettings]);

  // Read from the response, which may still be the one of the previous rows
  const isProducts = data?.rows === "products";
  const rowCount = data
    ? isProducts
      ? data.product_rows.length
      : data.aggregate_rows.length
    : 0;
  const narrowed = settings.find.trim() !== "" || settings.claiming !== null;
  const pages = data !== null && data.rows === "products" && data.total > data.limit;
  // In an exported image: the caption, the table, its legend and its pages
  // in words; none of the controls, the picker or the buttons. The rows as a
  // file are in the Export menu of the page (matrixExport).
  const capturing = useCapturing();

  return (
    <Box
      component="section"
      aria-label={ANALYTICS_VIEW_LABELS.matrix}
      sx={(theme) => panelSx(theme)}
    >
      {!capturing && (
        <MatrixControls
          settings={settings}
          columns={data?.shown_columns ?? []}
          onChange={setSettings}
        />
      )}

      {data && !capturing && (
        <MatrixCapabilities matrix={data} scope={scope} onChange={setSettings} />
      )}

      {data && (
        <Typography
          component="div"
          color="text.secondary"
          aria-label="Caption"
          sx={{ fontSize: 12, lineHeight: 1.5 }}
        >
          <WithFigures text={captionOf(data)} />
        </Typography>
      )}

      {!data ? (
        <Box sx={{ flex: 1, display: "grid", placeItems: "center", minHeight: 240, p: 3 }}>
          {read.isLoading ? (
            <LogoLoader size="view" surface="paper" label="Loading the matrix" />
          ) : (
            <Typography color="text.secondary">The matrix could not be loaded.</Typography>
          )}
        </Box>
      ) : rowCount === 0 ? (
        <Box sx={{ display: "grid", placeItems: "center", minHeight: 160, p: 3 }}>
          <Typography color="text.secondary" align="center">
            {narrowed
              ? "No row is left after narrowing the rows."
              : isProducts
                ? "No product of this scope has a claim in a column shown."
                : "Nothing of this scope to count in this table."}
          </Typography>
        </Box>
      ) : (
        <MatrixTable
          matrix={data}
          selection={selection}
          onPin={(pin) => setSettings({ pin })}
          onOpenEntity={onOpenEntity}
        />
      )}

      {data && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            columnGap: 2,
            rowGap: 1,
            flexWrap: "wrap",
          }}
        >
          <MatrixLegend matrix={data} />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              fontSize: 12.5,
              color: "text.secondary",
            }}
          >
            <Box component="span" aria-label="Pager">
              <WithFigures text={pagerText(data)} />
            </Box>
            {pages && !capturing && (
              <>
                <IconButton
                  size="small"
                  aria-label="Previous page"
                  disabled={settings.page <= 1}
                  onClick={() => setSettings({ page: settings.page - 1 })}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Next page"
                  disabled={data.offset + data.limit >= data.total}
                  onClick={() => setSettings({ page: settings.page + 1 })}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* The products picked for comparison, only where the rows are products */}
      {isProducts && !capturing && <CompareTrayBar />}
    </Box>
  );
};

export default MatrixView;
